import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { AthenaLogo } from '../components/AthenaLogo';

// Partículas simples subindo com trilha vertical
function RisingParticles() {
  const canvasRef = useRef(null);
  const animRef  = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const COUNT = 40;
    const particles = Array.from({length: COUNT}, () => ({
      x:     Math.random() * window.innerWidth,
      y:     Math.random() * window.innerHeight,
      vy:    0.4 + Math.random() * 0.8,      // sobe
      size:  1.2 + Math.random() * 2,
      alpha: 0.3 + Math.random() * 0.5,
      trail: [],                              // histórico de posições
      maxTrail: Math.floor(20 + Math.random() * 40),
    }));

    const draw = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      particles.forEach(p => {
        // Atualiza posição
        p.trail.push({x: p.x, y: p.y});
        if (p.trail.length > p.maxTrail) p.trail.shift();

        p.y -= p.vy;
        // Leve oscilação lateral
        p.x += Math.sin(p.y * 0.02) * 0.3;

        // Reset quando sai pelo topo
        if (p.y < -10) {
          p.y = H + 10;
          p.x = Math.random() * W;
          p.trail = [];
        }

        // Trilha vertical (linha fina)
        if (p.trail.length > 1) {
          const grad = ctx.createLinearGradient(
            p.trail[0].x, p.trail[0].y,
            p.trail[p.trail.length-1].x, p.trail[p.trail.length-1].y
          );
          grad.addColorStop(0, `rgba(60,130,255,0)`);
          grad.addColorStop(1, `rgba(60,140,255,${p.alpha * 0.4})`);
          ctx.beginPath();
          ctx.moveTo(p.trail[0].x, p.trail[0].y);
          p.trail.forEach(pt => ctx.lineTo(pt.x, pt.y));
          ctx.strokeStyle = grad;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }

        // Ponto brilhante na ponta
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(80,160,255,${p.alpha})`;
        ctx.fill();

        // Glow suave
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(60,120,255,${p.alpha * 0.15})`;
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} style={{position:'absolute',inset:0,pointerEvents:'none'}} />;
}

// Ícones inline para os campos
function IconEmail() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

function IconLock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

const fieldStyle = {
  width: '100%',
  padding: '14px 14px 14px 44px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 14,
  fontSize: 15,
  color: 'rgba(255,255,255,0.9)',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'Inter, system-ui, sans-serif',
  transition: 'border-color .2s',
};

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isRegister,   setIsRegister]   = useState(false);
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [name,         setName]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) await register(email, password, name);
      else await login(email, password);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : err?.response
            ? 'Credenciais inválidas. Tente novamente.'
            : 'Não foi possível conectar à Athena agora. Verifique sua internet e tente novamente.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#030610', position: 'relative', overflow: 'hidden',
      fontFamily: 'Inter, system-ui, sans-serif',
    }} data-testid="login-page">

      {/* Fundo escuro radial */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(20,50,120,0.25) 0%, transparent 70%)',
        pointerEvents: 'none',
      }}/>

      <RisingParticles />

      {/* Card central */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        style={{
          position: 'relative', zIndex: 10,
          width: '100%', maxWidth: 400,
          padding: '0 24px',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo oficial */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 40 }}>
          <AthenaLogo size={160} variant="vertical" showText={true} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {isRegister && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <IconUser/>
              </div>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Seu nome" style={fieldStyle}
                onFocus={e => e.target.style.borderColor = 'rgba(59,130,246,0.4)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                data-testid="name-input" />
            </motion.div>
          )}

          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <IconEmail/>
            </div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email" style={fieldStyle} autoComplete="email"
              onFocus={e => e.target.style.borderColor = 'rgba(59,130,246,0.4)'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              data-testid="email-input" />
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <IconLock/>
            </div>
            <input type={showPassword ? 'text' : 'password'} value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Senha" autoComplete="current-password"
              style={{ ...fieldStyle, paddingRight: 48 }}
              onFocus={e => e.target.style.borderColor = 'rgba(59,130,246,0.4)'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              data-testid="password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)',
                display: 'flex', alignItems: 'center' }}
              data-testid="toggle-password">
              {showPassword ? <EyeSlash size={17} /> : <Eye size={17} />}
            </button>
          </div>

          {error && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ fontSize: 13, color: '#f87171', textAlign: 'center', margin: 0 }}
              data-testid="auth-error">
              {error}
            </motion.p>
          )}

          <button type="submit" disabled={loading}
            style={{
              width: '100%', padding: '15px',
              background: loading ? 'rgba(59,130,246,0.3)' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
              border: 'none', borderRadius: 14,
              fontSize: 16, fontWeight: 600, color: '#fff',
              cursor: loading ? 'default' : 'pointer',
              transition: 'opacity .2s, transform .1s',
              fontFamily: 'Inter, system-ui, sans-serif',
              boxShadow: loading ? 'none' : '0 4px 20px rgba(59,130,246,0.35)',
            }}
            onMouseEnter={e => { if(!loading) e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            data-testid="submit-button">
            {loading ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff', borderRadius: '50%', margin: '0 auto' }}/>
            ) : isRegister ? 'Criar conta' : 'Entrar'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 20 }}>
          {isRegister ? 'Já tem uma conta?' : 'Não tem uma conta?'}{' '}
          <button onClick={() => { setIsRegister(!isRegister); setError(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              color: '#3b82f6', fontSize: 13, fontFamily: 'inherit' }}
            data-testid="toggle-auth-mode">
            {isRegister ? 'Entrar' : 'Criar conta'}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
